import React, { useEffect, useMemo, useState } from 'react';
import styles from '../llmSettingsTab/LlmSettingsTab.module.css';
import { settingsApi } from '../../../lib/apiClient';
import { toast } from '../../../lib/toast';
import { FieldMeta, SettingsMap } from '../../../types/webchatTabsTypes';
import SecureInput from '../../../ui/SecureInput/SecureInput';
import { hasValue, normalizeResponse } from '../settingsUtils';
import TokenSettingsTab from '../tokenSettingsTab/TokenSettingsTab';


const FIELD_META: FieldMeta[] = [
  {
    key: 'webchat_llm_provider',
    label: 'Provider',
    description: 'LLM provider used for webchat conversations.',
    type: 'select',
    fallbackDefault: 'anthropic',
    options: [
      { value: 'anthropic', label: 'Anthropic' },
      { value: 'openai', label: 'OpenAI' },
    ],
  },
  {
    key: 'webchat_llm_model',
    label: 'Model',
    description: 'Model name (e.g. claude-sonnet-4-20250514, gpt-4o). Empty = provider default.',
    type: 'string',
    fallbackDefault: '',
  },
  {
    key: 'webchat_llm_api_key_anthropic',
    label: 'Anthropic API Key',
    description: 'Anthropic API key for webchat. Overrides environment variable.',
    type: 'string',
    fallbackDefault: '',
    isSensitive: true,
    providers: ['anthropic'],
  },
  {
    key: 'webchat_llm_api_key_openai',
    label: 'OpenAI API Key',
    description: 'OpenAI API key for webchat. Overrides environment variable.',
    type: 'string',
    fallbackDefault: '',
    isSensitive: true,
    providers: ['openai'],
  },
  {
    key: 'webchat_llm_api_version',
    label: 'API Version',
    description: 'Anthropic API version header (e.g. 2023-06-01).',
    type: 'string',
    fallbackDefault: '',
    providers: ['anthropic'],
  },
  {
    key: 'webchat_llm_max_tokens',
    label: 'Max Response Tokens',
    description: 'Maximum tokens per single LLM response.',
    type: 'number',
    fallbackDefault: '',
    min: 1,
    step: 1,
  },
  {
    key: 'webchat_llm_temperature',
    label: 'Temperature',
    description: 'Sampling temperature (0–1).',
    type: 'number',
    fallbackDefault: '',
    min: 0,
    max: 1,
    step: 'any',
  },
  {
    key: 'webchat_token_monthly_limit',
    label: 'Monthly Token Limit',
    description: 'Maximum total tokens allowed per calendar month across all webchat conversations.',
    type: 'number',
    fallbackDefault: '',
    min: 0,
    step: 100000,
  },
];

type InnerTab = 'settings' | 'token-settings';


const ChatLlmSettingsTab: React.FC = () => {
  const [innerTab, setInnerTab] = useState<InnerTab>('settings');

  const [settings, setSettings] = useState<SettingsMap | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [initialDraft, setInitialDraft] = useState<Record<string, string>>({});
  const [sensitiveConfigured, setSensitiveConfigured] = useState<Record<string, boolean>>({});
  const [clearedSensitive, setClearedSensitive] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadSettings = async (showErrorToast = true) => {
    try {
      const raw = await settingsApi.getWebchatSettings();
      const fromServer = normalizeResponse(raw);
      const normalized: SettingsMap = { ...fromServer };
      const initial: Record<string, string> = {};
      const sensitiveState: Record<string, boolean> = {};

      for (const { key, fallbackDefault, isSensitive } of FIELD_META) {
        const rawValue = fromServer[key]?.value;
        const rawDefault = fromServer[key]?.default;
        const def = hasValue(rawDefault) ? (rawDefault as string | number) : fallbackDefault;
        const fieldIsSensitive = fromServer[key]?.isSensitive ?? isSensitive;

        if (fieldIsSensitive) {
          sensitiveState[key] = hasValue(rawValue) || hasValue(rawDefault);
          normalized[key] = { value: '', default: '' };
          initial[key] = '';
        } else {
          const value = hasValue(rawValue) ? (rawValue as string | number) : def;
          normalized[key] = { value, default: def };
          initial[key] = String(value);
        }
      }

      setSettings(normalized);
      setDraft(initial);
      setInitialDraft(initial);
      setSensitiveConfigured(sensitiveState);
      setClearedSensitive({});
    } catch {
      if (showErrorToast) toast.error('Failed to load Chat LLM settings');
    }
  };
  useEffect(() => {
    loadSettings()
      .finally(() => setLoading(false));
  }, []);

  const isDirty = useMemo(() => {
    return FIELD_META.some(({ key }) => draft[key] !== (initialDraft[key] ?? '') || clearedSensitive[key] === true);
  }, [draft, initialDraft, clearedSensitive]);

  const handleSensitiveChange = (key: string, value: string) => {
    setDraft(d => ({ ...d, [key]: value }));
    if (value.trim()) {
      setClearedSensitive(c => ({ ...c, [key]: false }));
    }
  };

  const handleClearSensitive = (key: string) => {
    setDraft(d => ({ ...d, [key]: '' }));
    setClearedSensitive(c => ({ ...c, [key]: true }));
  };

  const handleSaveAll = async (e: React.FormEvent) => {
    e.preventDefault();
    for (const { key, label, type, min } of FIELD_META) {
      const raw = draft[key]?.trim();
      if (type === 'number') {
        const num = Number(raw);
        if (isNaN(num)) {
          toast.error(`${label}: please enter a valid number`);
          return;
        }
        if (min !== undefined && num < min) {
          toast.error(`${label}: must be >= ${min}`);
          return;
        }
      }
    }
    setSaving(true);
    try {
      const updates: Record<string, string> = {};
      for (const { key, type, isSensitive } of FIELD_META) {
        const raw = draft[key]?.trim() ?? '';
        const previous = initialDraft[key] ?? '';
        const clearRequested = clearedSensitive[key] === true;
        if (isSensitive) {
          if (!raw && !clearRequested) continue;
          if (raw) {
            updates[key] = raw;
          } else if (clearRequested) {
            updates[key] = '';
          }
          continue;
        }
        if (raw === previous) continue;
        if (type === 'number') {
          updates[key] = String(Number(raw));
        } else {
          updates[key] = raw;
        }
      }

      if (Object.keys(updates).length === 0) {
        toast.success('No changes to save');
        setSaving(false);
        return;
      }

      await Promise.all(
        Object.entries(updates).map(([key, value]) =>
          settingsApi.setValue(key, { value })
        )
      );
      await loadSettings(false);
      toast.success('Chat LLM settings saved');
    } catch {
      toast.error('Failed to save Chat LLM settings');
    } finally {
      setSaving(false);
    }
  };

  const handleResetAll = async () => {
    if (!settings) return;
    const updates: Record<string, string> = {};
    for (const { key, fallbackDefault, isSensitive } of FIELD_META) {
      if (isSensitive) continue;
      const currentDefault = settings[key]?.default ?? fallbackDefault;
      updates[key] = String(currentDefault);
    }
    if (Object.keys(updates).length === 0) return;
    setSaving(true);
    try {
      await Promise.all(
        Object.entries(updates).map(([key, value]) =>
          settingsApi.setValue(key, { value })
        )
      );
      await loadSettings(false);
      toast.success('Reset non-sensitive settings to defaults');
    } catch {
      toast.error('Failed to reset settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className={styles.loading}>Loading…</div>;
  if (!settings) return null;



  return (
    <div className={styles.tab}>
      <div className={styles.header}>
        <div>
          <h2>Chat LLM</h2>
          <p>LLM provider, model, and parameters used for all webchat AI agent conversations.</p>
        </div>
      </div>
      <div className={styles.innerTabs}>
        <button
          className={`${styles.innerTab} ${innerTab === 'settings' ? styles.innerTabActive : ''}`}
          onClick={() => setInnerTab('settings')}
        >
          Settings
        </button>
        <button
          className={`${styles.innerTab} ${innerTab === 'token-settings' ? styles.innerTabActive : ''}`}
          onClick={() => setInnerTab('token-settings')}
        >
          Token Settings
        </button>
      </div>

      {innerTab === 'settings' && (
        <form className={styles.form} onSubmit={handleSaveAll}>
          {FIELD_META.filter(({ providers }) => {
            if (!providers) return true;
            const selectedProvider = draft['webchat_llm_provider'] ?? 'anthropic';
            return providers.includes(selectedProvider);
          }).map(({ key, label, description, type, isSensitive, min, max, step, options }) => (
            <div key={key} className={styles.fieldRow}>
              <div className={styles.fieldHeader}>
                <span className={styles.fieldLabel}>{label}</span>
                <span className={styles.keyBadge}>{key}</span>
              </div>
              <p className={styles.fieldDescription}>{description}</p>
              <div className={styles.fieldInputRow}>
                {type === 'select' && options ? (
                  <select
                    className={styles.input}
                    value={draft[key] ?? ''}
                    onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))}
                  >
                    {options.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                ) : (
                  <div className={styles.fieldInputControls}>
                    <SecureInput
                      fieldType={type === 'number' ? 'number' : 'text'}
                      value={draft[key] ?? ''}
                      placeholder={isSensitive && sensitiveConfigured[key] ? 'Enter a new value to replace the configured key' : undefined}
                      autoComplete={isSensitive ? 'new-password' : 'off'}
                      min={type === 'number' ? min : undefined}
                      max={type === 'number' ? max : undefined}
                      step={type === 'number' ? (step ?? 1) : undefined}
                      onChange={(value: string) => isSensitive ? handleSensitiveChange(key, value) : setDraft(d => ({ ...d, [key]: value }))}
                    />
                    {isSensitive && sensitiveConfigured[key] && (
                      <button
                        type="button"
                        className={styles.clearBtn}
                        onClick={() => handleClearSensitive(key)}
                        disabled={saving}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                )}
                <span className={styles.defaultHint}>
                  {isSensitive
                    ? clearedSensitive[key]
                      ? '(will be cleared)'
                      : sensitiveConfigured[key]
                        ? '(configured)'
                        : '(not set)'
                    : `default: ${settings[key]?.default}`}
                </span>
              </div>
            </div>
          ))}

          <div className={styles.footer}>
            <button type="submit" className={styles.saveBtn} disabled={!isDirty || saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className={styles.resetBtn} disabled={saving} onClick={handleResetAll}>
              Reset all to defaults
            </button>
          </div>
        </form>
      )}
      {innerTab === 'token-settings' && (
        <TokenSettingsTab />
      )}
    </div>
  );
};

export default ChatLlmSettingsTab;
